import React, {
  CSSProperties,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Delete,
  Search,
  Check,
  Add,
  ArrowBack,
  Edit,
} from "@mui/icons-material";
import {
  Box,
  IconButton,
  ListItemDecorator,
  ListItemContent,
  Avatar,
  ListItemButton,
  ListItem as MUIListItem,
  Input,
  CircularProgress,
  Typography,
  LinearProgress,
} from "@mui/joy";
import { useAuthorization } from "context/AuthorizationContext";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import InfiniteLoader from "react-window-infinite-loader";
import { FixedSizeList, areEqual } from "react-window";
import { Artist, ListResponse } from "types";
import { GridSortModel } from "@mui/x-data-grid";
import axios from "axios";
import { toast } from "sonner";
import AutoSizer, { Size } from "react-virtualized-auto-sizer";
import { usePrevious } from "hooks";
import { useDialogManger } from "context/DialogManagerContext";
import { CustomNoRowsOverlay } from "components/styled_components/DataGridOverlays";
import ArtistDetailsModal from "views/Admin/Artists/DetailsModal";

const ListItem = memo(
  ({
    data,
    index,
    style,
  }: {
    data: {
      artists: Artist[];
      selectedRows: string[];
      isItemLoaded: (index: number) => boolean;
      setDetailsOpen: React.Dispatch<
        React.SetStateAction<{
          open: boolean;
          artistId: string;
        }>
      >;
      setSelectedRows: React.Dispatch<React.SetStateAction<string[]>>;
    };
    index: number;
    style: CSSProperties;
  }) => {
    const {
      artists,
      selectedRows,
      isItemLoaded,
      setDetailsOpen,
      setSelectedRows,
    } = data;
    if (!isItemLoaded(index)) {
      return (
        <MUIListItem style={style}>
          <CircularProgress size="sm" variant="plain" />
        </MUIListItem>
      );
    }
    return (
      <ListItemButton
        style={style}
        sx={{
          px: 2,
          gap: 2,
        }}
        onClick={() =>
          setDetailsOpen({ open: true, artistId: artists[index].id })
        }
      >
        <ListItemDecorator>
          <Avatar
            color={
              selectedRows.includes(artists[index].id) ? "primary" : "neutral"
            }
            onClick={(e) => {
              e.stopPropagation();
              if (!selectedRows.includes(artists[index].id)) {
                setSelectedRows([...selectedRows, artists[index].id]);
              } else {
                setSelectedRows(
                  selectedRows.filter((r) => r !== artists[index].id)
                );
              }
            }}
          >
            {selectedRows.includes(artists[index].id) ? (
              <Check />
            ) : (
              <>{artists[index].name[0].toUpperCase()}</>
            )}
          </Avatar>
        </ListItemDecorator>
        <ListItemContent>
          <Typography>{artists[index].name}</Typography>
          <Typography level="body2" noWrap>
            {artists[index].address}
          </Typography>
        </ListItemContent>
      </ListItemButton>
    );
  },
  areEqual
);

const ArtistsMobileList: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [detailsOpen, setDetailsOpen] = useState({ open: false, artistId: "" });
  const [artists, setArtists] = useState<Artist[]>([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [totalItems, setTotalItems] = useState(0);
  const [sort] = useState<GridSortModel>([
    { field: "created_at", sort: "asc" },
  ]);
  const [filter, setFilter] = useState("");
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [t] = useTranslation();
  const navigate = useNavigate();
  const { showDialog } = useDialogManger();
  const { isAllowed } = useAuthorization();
  let filterTimeout = useRef<NodeJS.Timeout>(null!);
  const prevFilter = usePrevious(filter);
  const itemCount =
    artists.length < totalItems ? artists.length + 1 : artists.length;

  const fetchArtists = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get<ListResponse<Artist>>("/api/artists", {
        params: {
          page: page,
          limit,
          sort: `${sort[0].field}:${sort[0].sort}`,
          search: filter,
        },
      });
      setArtists(data.data);
      setPage(data.page);
      setTotalItems(data.total);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        //Fore the user logout if the request is unauthorized
        if (error.response?.status === 401) {
          navigate("/login");
          return;
        }
        console.log(error.response?.data);
        toast.error(error.response?.data.error);
      } else {
        console.log(error);
        toast.error(t("Something went wrong! Cannot fetch artists"));
      }
    }
    setLoading(false);
  }, [page, limit, sort, filter, navigate, t]);

  const fetchMoreArtists = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get<ListResponse<Artist>>("/api/artists", {
        params: {
          page: page + 1,
          limit,
          sort: `${sort[0].field}:${sort[0].sort}`,
          search: filter,
        },
      });
      setArtists([...artists, ...data.data]);
      setPage(data.page);
      setTotalItems(data.total);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        //Fore the user logout if the request is unauthorized
        if (error.response?.status === 401) {
          navigate("/login");
          return;
        }
        console.log(error.response?.data);
        toast.error(error.response?.data.error);
      } else {
        console.log(error);
        toast.error(t("Something went wrong! Cannot fetch artists"));
      }
    }
    setLoading(false);
  };

  const isItemLoaded = (index: number) =>
    !(artists.length < totalItems) || index < artists.length;

  const loadMoreItems = loading ? () => {} : fetchMoreArtists;

  const onDeleteArtists = async () => {
    try {
      const { status } = await axios.post(`/api/artists/delete`, {
        artistIds: selectedRows,
      });
      if (status === 200) {
        setSelectedRows([]);
        fetchArtists();
        toast.success(t("Artists successfully deleted"));
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        //Fore the user logout if the request is unauthorized
        if (error.response?.status === 401) {
          navigate("/login");
          return;
        }
        console.log(error.response?.data);
        toast.error(error.response?.data.error);
      } else {
        console.log(error);
        toast.error(t("Something went wrong! Cannot delete artists"));
      }
    }
    setLoading(false);
  };

  const onDeleteClick = () => {
    showDialog({
      title: "Delete the selected artists?",
      body: "If you proceed with this action, the selected artists will be deleted.",
      actions: [
        {
          label: "Cancel",
          color: "neutral",
          variant: "plain",
          onClick: (e, closeModal) => {
            closeModal();
          },
        },
        {
          label: "Delete",
          color: "danger",
          variant: "solid",
          onClick: (e, closeModal) => {
            onDeleteArtists();
            closeModal();
          },
        },
      ],
    });
  };

  useEffect(() => {
    if (prevFilter !== filter && prevFilter !== undefined) {
      if (filterTimeout.current) {
        clearTimeout(filterTimeout.current);
      }
      filterTimeout.current = setTimeout(() => {
        fetchArtists();
      }, 500);

      return () => {
        clearTimeout(filterTimeout.current);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, prevFilter]);

  useEffect(() => {
    fetchArtists();
  }, [fetchArtists]);

  return (
    <>
      <Box position={"absolute"} top={0} left={0} width={"100vw"}>
        {loading ? <LinearProgress /> : null}
      </Box>
      <Typography level="h4" fontWeight={600} pt={2} px={2}>
        {t("Artists")}
      </Typography>
      <Box
        display={"flex"}
        flexDirection={"row"}
        width={"100%"}
        py={2}
        px={1}
        boxSizing={"border-box"}
        justifyContent={selectedRows.length > 0 ? "flex-end" : "flex-start"}
        alignItems={"center"}
      >
        {selectedRows.length > 0 &&
        (isAllowed("artists.write") || isAllowed("artists.delete")) ? (
          <Box
            display={"flex"}
            flexDirection={"row"}
            flex={"1 1"}
            justifyContent={"space-between"}
            alignItems={"center"}
          >
            <Box
              display={"flex"}
              flexDirection={"row"}
              flex={"1 1"}
              alignItems={"center"}
              gap={2}
            >
              <IconButton
                variant="plain"
                color="neutral"
                onClick={() => setSelectedRows([])}
              >
                <ArrowBack />
              </IconButton>
              <Typography level="h6">{selectedRows.length}</Typography>
            </Box>
            <Box
              display={"flex"}
              flexDirection={"row"}
              flex={"1 1"}
              alignItems={"center"}
              justifyContent={"flex-end"}
            >
              {selectedRows.length === 1 && isAllowed("artists.write") ? (
                <IconButton
                  variant="plain"
                  color="neutral"
                  onClick={() => navigate(selectedRows[0])}
                >
                  <Edit />
                </IconButton>
              ) : null}
              {isAllowed("artists.delete") ? (
                <IconButton
                  variant="plain"
                  color="danger"
                  onClick={onDeleteClick}
                >
                  <Delete />
                </IconButton>
              ) : null}
            </Box>
          </Box>
        ) : (
          <Input
            size="sm"
            startDecorator={<Search />}
            fullWidth
            value={filter}
            placeholder={`${t("Type to search")}...`}
            onChange={(e) => setFilter(e.currentTarget.value)}
          />
        )}
      </Box>
      {isAllowed("artists.write") ? (
        <IconButton
          sx={{
            position: "absolute",
            right: "1rem",
            bottom: "1rem",
            zIndex: 9,
            // boxShadow:
            //   "rgba(0, 0, 0, 0.05) 0px 3px 5px -1px, rgba(0, 0, 0, 0.10) -1px 1px 10px 0px, rgba(0, 0, 0, 0.10) 0px 1px 18px 0px",
          }}
          size="lg"
          onClick={() => navigate("new")}
        >
          <Add fontSize="large" />
        </IconButton>
      ) : null}
      <Box flex={"1 1 auto"} boxSizing={"border-box"} py={1}>
        <AutoSizer>
          {({ height, width }: Size) => (
            <InfiniteLoader
              threshold={5}
              itemCount={itemCount}
              isItemLoaded={isItemLoaded}
              loadMoreItems={loadMoreItems}
            >
              {({ onItemsRendered, ref }) => (
                <FixedSizeList
                  itemCount={itemCount}
                  onItemsRendered={onItemsRendered}
                  ref={ref}
                  height={height}
                  width={width}
                  itemSize={60}
                  itemData={{
                    artists,
                    selectedRows,
                    isItemLoaded,
                    setDetailsOpen,
                    setSelectedRows,
                  }}
                  innerElementType={
                    artists.length === 0
                      ? () => <CustomNoRowsOverlay label="No artists found" />
                      : "div"
                  }
                >
                  {ListItem}
                </FixedSizeList>
              )}
            </InfiniteLoader>
          )}
        </AutoSizer>
      </Box>
      <ArtistDetailsModal
        layout="fullscreen"
        open={detailsOpen.open}
        artistId={detailsOpen.artistId}
        onClose={() => {
          setDetailsOpen({ open: false, artistId: "" });
        }}
      />
    </>
  );
};

export default ArtistsMobileList;
